import { formatNumber } from '@angular/common';

import { getAccountFullName } from 'app/utils/account';
import { parseNumber } from 'app/utils/numbers-utils';
import { AppLooseArticle as LooseArticle } from 'graphql/models/extended';
import {
  Account, Customer, Object as Objekt, Branch, Address, ContactPerson, Article
} from 'graphql/models/generated';
import { AccountStatus } from 'models/account.model';
import { ConditionTypeENUM } from 'models/condition.model';
import {
  DataTableResourceType,
  DataTableConfig as TableConfig,
  DataTableAction
} from 'models/data.model';

export function buildAddressArrayFull(address: Address): string[] {
  const result: string[] = [];
  const addressLine = `${address.streetName || ''}`
    + `${(address.streetName && address.streetNr) ? ' ' : ''}`
    + `${address.streetNr || ''}`
    + `${((address.streetName || address.streetNr) && (address.zipCode || address.city)) ? ', ' : ''}`
    + `${address.zipCode || ''}`
    + `${(address.zipCode && address.city) ? ' ' : ''}`
    + `${address.city || ''}`;

  if (addressLine !== '') {
    result.push(addressLine);
  }
  if (address.country) {
    if (addressLine !== '') {
      result.push(', ');
    }
    result.push(`FORMS.COUNTRY_LIST.${address.country.toUpperCase()}`);
  }

  return result;
}

export function buildAddressArrayPart(address: Address): string[] {
  const result: string[] = [];
  if (address.zipCode) {
    result.push(address.zipCode, ', ');
  }
  if (address.city) {
    result.push(address.city, ', ');
  }
  if (address.country) {
    result.push(`FORMS.COUNTRY_LIST.${address.country.toUpperCase()}`, ', ');
  }

  return (result.length ? result.slice(0, -1) : result);
}

export function buildAddressArray(address: Address, full = true): string[] {
  return (full ? buildAddressArrayFull(address) : buildAddressArrayPart(address));
}

export const CUSTOMERS_TABLE_CONFIG: TableConfig = {
  searchTranslationKeys: ['TABLE.CUSTOMERS.NAME', 'TABLE.CUSTOMERS.NR', 'TABLE.CUSTOMERS.ADDITIONAL_NAMES'],
  displayedColumns: ['name', 'additionalNames', 'id', 'customerNumber', 'addresses'],
  actions: [
    { value: 'newOffer', title: 'GENERAL.NEW_OFFER' },
    { value: 'edit', title: 'GENERAL.EDIT' },
    { value: 'delete', title: 'GENERAL.DELETE' }
  ],
  columnDefs: [
    { id: 'name', title: 'TABLE.CUSTOMERS.NAME', sortKeys: ['name'] },
    {
      id: 'additionalNames',
      title: 'TABLE.CUSTOMERS.ADDITIONAL_NAMES',
      displayFn(c: Customer): string {
        return c.additionalNames?.join('<br>') ?? '';
      }
    },
    { id: 'id', title: 'TABLE.CUSTOMERS.NR', sortKeys: ['id'] },
    { id: 'customerNumber', title: 'TABLE.CUSTOMERS.NR', sortKeys: ['customerNumber'] },
    {
      id: 'addresses',
      title: 'TABLE.CUSTOMERS.ADDRESS',
      getAddressArray(c: Customer): string[] {
        return (c.addresses && c.addresses[0]) ? buildAddressArray(c.addresses[0]) : [];
      }
    }
  ]
};

export const CONTACT_PERSONS_TABLE_CONFIG: TableConfig = {
  searchTranslationKeys: ['TABLE.CONTACT_PERSON.NAME'],
  displayedColumns: ['name', 'phone', 'email'],
  actions: [
    { value: 'newOffer', title: 'GENERAL.NEW_OFFER' },
    { value: 'edit', title: 'GENERAL.EDIT' },
    { value: 'delete', title: 'GENERAL.DELETE' }
  ],
  columnDefs: [
    {
      id: 'name',
      title: 'TABLE.CONTACT_PERSON.NAME',
      sortKeys: ['firstName', 'lastName'],
      valueFn(c: ContactPerson): string {
        return `${c.firstName} ${c.lastName}`;
      },
      displayFn(c: ContactPerson): string {
        return this.valueFn ? this.valueFn(c) as string : '';
      }
    },
    { id: 'phone', title: 'TABLE.CONTACT_PERSON.PHONE', sortKeys: ['phone'] },
    { id: 'email', title: 'TABLE.CONTACT_PERSON.EMAIL', sortKeys: ['email'] }
  ]
};

export const OBJEKTS_TABLE_CONFIG: TableConfig = {
  searchTranslationKeys: ['TABLE.OBJECTS.NAME', 'TABLE.OBJECTS.NR'],
  displayedColumns: ['name', 'nameForDealery', 'objectNumber', 'address'],
  actions: [
    { value: 'newOffer', title: 'GENERAL.NEW_OFFER' },
    { value: 'edit', title: 'GENERAL.EDIT' },
    { value: 'delete', title: 'GENERAL.DELETE' },
    { value: 'acceptObject', title: 'GENERAL.ACCEPT_OBJECT_DATA' }
  ],
  columnDefs: [
    {
      id: 'nameForDealery',
      title: 'TABLE.OBJECTS.NAME',
      sortKeys: ['name'],
      displayFn(o: Objekt): string {
        let name = o.name || '';

        return name;
      }
    },
    { id: 'name', title: 'TABLE.OBJECTS.NAME', sortKeys: ['name'] },
    {
      id: 'objectNumber',
      title: 'TABLE.OBJECTS.NR',
      sortKeys: ['objectNumber'],
      valueFn(o: Objekt): string {
        return o.objectNumber || o.id || '';
      },
      displayFn(o: Objekt): string {
        return this.valueFn ? this.valueFn(o) as string : '';
      }
    },
    {
      id: 'address',
      title: 'TABLE.OBJECTS.ADDRESS',
      getAddressArray(o: Objekt): string[] {
        return o.address ? buildAddressArray(o.address) : [];
      }
    }
  ],
  getActionsFilteredByRow(o: Objekt, actions: DataTableAction[]) {
    if (!o.createdByManufacturer) {
      return actions.filter((action) => action.value !== 'acceptObject');
    }

    return actions;
  }
};

export const CONDITIONS_TABLE_CONFIG: TableConfig = {
  searchTranslationKeys: ['TABLE.CONDITIONS.CUSTOMER_NAME', 'TABLE.CONDITIONS.CUSTOMER_NR'],
  displayedColumns: ['name', 'id', 'customerNumber', 'ek', 'lp'],
  actions: [
    { value: 'edit', title: 'GENERAL.EDIT' },
    { value: 'delete', title: 'GENERAL.DELETE' }
  ],
  columnDefs: [
    { id: 'name', title: 'TABLE.CONDITIONS.CUSTOMER', sortKeys: ['name'] },
    { id: 'id', title: 'TABLE.CONDITIONS.CUSTOMER_NR', sortKeys: ['id'] },
    { id: 'customerNumber', title: 'TABLE.CONDITIONS.CUSTOMER_NR', sortKeys: ['customerNumber'] },

    {
      id: 'ek',
      valueFn(c: Customer): number | string {
        return (c.conditions && (c.conditions.type === ConditionTypeENUM.SURCHARGE))
          ? (c.conditions.surcharge) : '';
      },
      title: 'TABLE.CONDITIONS.EK',
      displayFn(c: Customer): string {
        return this.valueFn ? this.valueFn(c) as string : '';
      }
    }, {
      id: 'lp',
      valueFn(c: Customer): number | string {
        return (c.conditions && (c.conditions.type === ConditionTypeENUM.DISCOUNT))
          ? (c.conditions.discount) : '';
      },
      title: 'TABLE.CONDITIONS.LP',
      displayFn(c: Customer): string {
        return this.valueFn ? this.valueFn(c) as string : '';
      }
    }
  ]
};

export const ARTICLES_TABLE_CONFIG: TableConfig = {
  searchTranslationKeys: ['TABLE.ARTICLE.MNR', 'TABLE.ARTICLE.EAN', 'TABLE.ARTICLE.NAME'],
  displayedColumns: ['mnr', 'ean', 'name', 'category.displayName', 'unit', 'listPrice', 'purchasePrice'],
  actions: [
    { value: 'edit', title: 'GENERAL.EDIT' },
    { value: 'delete', title: 'GENERAL.DELETE' }
  ],
  columnDefs: [
    { id: 'mnr', title: 'TABLE.ARTICLE.MNR', sortKeys: ['name'] },
    { id: 'ean', title: 'TABLE.ARTICLE.EAN', sortKeys: ['name'] },
    { id: 'name', title: 'TABLE.ARTICLE.NAME', sortKeys: ['name'] },
    { id: 'category.displayName', title: 'TABLE.ARTICLE.CATEGORY' },
    { id: 'unit', title: 'TABLE.ARTICLE.UNIT', sortKeys: ['unit'] },
    {
      id: 'listPrice',
      title: 'TABLE.ARTICLE.LIST_PRICE',
      displayFn(a: Article): string {
        return `<div class="text-nowrap">€ ${formatNumber(a.listPrice || 0, 'de-DE', '0.2-2')}</div>`;
      }
    },
    {
      id: 'purchasePrice',
      title: 'TABLE.ARTICLE.PURCHASE_PRICE',
      displayFn(a: Article): string {
        return `<div class="text-nowrap">€ ${formatNumber(a.purchasePrice || 0, 'de-DE', '0.2-2')}</div>`;
      }
    }
  ]
};

export const LOOSE_ARTICLES_TABLE_CONFIG: TableConfig = {
  searchTranslationKeys: ['TABLE.LOOSE_ARTICLE.SEARCH'],
  displayedColumns: ['image', 'description', 'listPrice'],
  columnDefs: [
    {
      id: 'image',
      title: 'TABLE.LOOSE_ARTICLE.IMAGE',
      displayFn(a: LooseArticle): string {
        return `<div class="image">
                    <img src="${a.externalImage}" loading="lazy">
                </div>`;
      }
    },
    { id: 'description', title: 'TABLE.LOOSE_ARTICLE.NAME' },
    {
      id: 'listPrice',
      title: 'TABLE.LOOSE_ARTICLE.LIST_PRICE',
      displayFn(a: LooseArticle): string {
        const listPrice = a.properties && a.properties.find((p) => (p.name === 'ListPrice'));

        return `<div class="text-nowrap">
                    € ${formatNumber(parseNumber(listPrice?.value ?? '0'), 'de-DE', '0.2-2')}
                </div>`;
      }
    }
  ]
};

export const ACCOUNT_TABLE_CONFIG: TableConfig = {
  searchTranslationKeys: ['TABLE.ACCOUNT.NAME', 'TABLE.ACCOUNT.EMAIL'],
  displayedColumns: ['name', 'username', 'branches', 'role'],
  actions: [
    { value: 'edit', title: 'GENERAL.EDIT' },
    { value: 'deactivate', title: 'SETTINGS.TEAM_DETAILS.DEACTIVATE' }
  ],
  columnDefs: [
    {
      id: 'name',
      title: 'TABLE.ACCOUNT.NAME',
      // Temporary disable, because there is an issue with sorting by relations
      // TODO: implement sorting by relations: DIT-924
      // sortKeys: ['name'],
      valueFn(acc: Account): string {
        return getAccountFullName(acc);
      },
      displayFn(acc: Account): string {
        return this.valueFn ? this.valueFn(acc) as string : '';
      },
      getImagePath(acc: Account): string {
        // TODO get image from media service
        return acc.id === 'U-1001' ? 'https://www.placecage.com/200/200' : '';
      }
    },
    { id: 'username', title: 'TABLE.ACCOUNT.EMAIL', sortKeys: ['username'] },
    {
      id: 'branches',
      title: 'TABLE.ACCOUNT.BRANCHES',
      // Temporary disable, because there is an issue with sorting by relations
      // TODO: implement sorting by relations: DIT-924
      // sortKeys: ['branches'],
      valueFn(acc: Account): string {
        return (acc.branches || []).map((branch: Branch) => branch.name).join(',');
      },
      displayFn(acc: Account): string {
        return this.valueFn ? this.valueFn(acc) as string : '';
      }
    },
    {
      id: 'role',
      title: 'TABLE.ACCOUNT.ROLE',
      // Temporary disable, because there is an issue with sorting by relations
      // TODO: implement sorting by relations: DIT-924
      // sortKeys: ['role'],
      valueFn(acc: Account): string {
        const branchRoles = acc.accountRoles?.branchRoles || [];

        return (branchRoles.length && branchRoles[0].role) || '';
      },
      displayFn(acc: Account): string {
        const role = this.valueFn ? this.valueFn(acc) as string : '';

        return role ? `FORMS.ACCOUNT_ROLE.${role}` : '';
      }
    }
  ],

  getCustomActionsData(acc: Account): any {
    if (acc.status === 'DEACTIVATED') {
      return {
        actions: [{ value: 'activate', title: 'SETTINGS.TEAM_DETAILS.ACTIVATE' }],
        label: 'SETTINGS.TEAM.DEACTIVATED'
      };
    }

    if (acc.status === 'INVITED') {
      return {
        actions: [],
        label: 'SETTINGS.TEAM.INVITED'
      };
    }

    return {
      actions: this.actions,
      label: ''
    };
  },

  rowDef: {
    isDisabled(acc: Account): boolean {
      return acc.status === AccountStatus.INVITED || acc.status === AccountStatus.DEACTIVATED;
    }
  }
};

export const BRANCH_TABLE_CONFIG: TableConfig = {
  searchTranslationKeys: ['TABLE.BRANCH.NAME', 'TABLE.BRANCH.ADDRESS', 'TABLE.BRANCH.CITY'],
  displayedColumns: ['name', 'address', 'city'],
  columnDefs: [
    {
      id: 'name', title: 'TABLE.BRANCH.NAME', sortKeys: ['name']
    },
    {
      id: 'address',
      title: 'TABLE.BRANCH.ADDRESS',
      valueFn(branch: Branch): string {
        return branch.address
          ? `${branch.address.streetName || ''} `
          + `${branch.address.streetNr || ''}`
          : '';
      },
      displayFn(branch: Branch): string {
        return this.valueFn ? this.valueFn(branch) as string : '';
      }
    },
    {
      id: 'city',
      title: 'TABLE.BRANCH.CITY',
      sortKeys: ['city'],
      getAddressArray(branch: Branch): string[] {
        return (branch.address ? buildAddressArray(branch.address, false) : []);
      }
    }
  ]
};

export const DATA_TABLE_CONFIG: Record<DataTableResourceType, TableConfig> = {
  Customer: CUSTOMERS_TABLE_CONFIG,
  ContactPerson: CONTACT_PERSONS_TABLE_CONFIG,
  Object: OBJEKTS_TABLE_CONFIG,
  Condition: CONDITIONS_TABLE_CONFIG,
  Article: ARTICLES_TABLE_CONFIG,
  LooseArticle: LOOSE_ARTICLES_TABLE_CONFIG,
  CustomersWithoutCondition: CUSTOMERS_TABLE_CONFIG,
  Account: ACCOUNT_TABLE_CONFIG,
  Branch: BRANCH_TABLE_CONFIG
};
